// External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

// Local libs
import Service from '../common/service';
import ProcessModel from './process.model';

/**
 * Service for managing processes (Master Data).
 *
 * Provides methods to retrieve and manage process data across various components.
 *
 * @module ProcessService
 */
@Injectable({
  providedIn: 'root'
})
export class ProcessService extends Service<ProcessModel> {

  /** Stores ongoing HTTP request subscriptions for cancellation. */
  public ongoingRequests: Subscription[] = [];

  /**
   * Creates an instance of ProcessService.
   * 
   * @param httpClient - The HttpClient service for making HTTP requests.
   * @param router - The Router service for navigation.
   */
  constructor(httpClient: HttpClient, router: Router) {
    super(httpClient, '/bh/process/');
  }

  /**
   * Retrieves the list of processes (Master Data).
   *
   * Used in:
   *  - {@link ProcessComponent} for listing processes.
   *  - {@link StagesComponent} for selecting a process while creating a stage.
   *  - {@link ActivityMasterComponent} for selecting a process while creating an activity master.
   *
   * @returns A promise resolving to an array of ProcessModel objects.
   * @throws Error message if HTTP request fails.
   */
  public getProcess(): Promise<Array<ProcessModel>> {
    return new Promise<Array<ProcessModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<ProcessModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000&order=new`, this.httpOptions)
        .subscribe(
          response => resolve(response),
          (error: HttpErrorResponse) => reject(error)
        );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests.
   *
   * Ensures proper cleanup and prevents memory leaks by unsubscribing from all observables.
   * Typically called in {@link ProcessComponent}.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(request => {
      if (request?.unsubscribe) {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
